import {useState, useMemo, Fragment} from 'react'
import {ContentArea} from '@/components/shared/ContentArea'
import {useParams} from 'react-router-dom'
import {XCircle, FileText} from 'lucide-react'
import {ErrorLogLink} from '@/components/ErrorLogLink'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {usePageTitle} from '@/hooks/use-page-title'
import {useExecutionStream} from '@/hooks/use-execution-stream'
import {useExecution} from '@/features/jobs/hooks/useExecution'
import {useExecutionMutations} from '@/features/jobs/hooks/useExecutionMutations'
import {useExecutionPermissions} from '@/features/jobs/hooks/useExecutionPermissions'
import {Button} from '@/components/ui/button'
import {Card, CardHeader, CardTitle, CardContent} from '@/components/ui/card'
import {Badge} from '@/components/ui/badge'
import {Progress} from '@/components/ui/progress'
import {ExecutionLogViewer} from '@/components/execution-log-viewer'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {CONCEPT_ICON} from '@/lib/concepts'
import {useI18n} from '@/i18n'
import {Stack} from '@/components/shared/Stack'
import {JobExecutionStatus, TaskExecutionStatus, TaskType} from '@/types/background-jobs'
import type {TaskExecutionTreeItem} from '@/types/background-jobs'
import {formatTimestamp, formatDuration} from '@/lib/date-utils'
import {StatusBadge, StatusBadgeType} from '@/components/shared/StatusBadge'
import {CodeText} from '@/components/shared/CodeText'
import {CardSection} from '@/components/shared/CardSection'
import {TextLink} from '@/components/shared/TextLink'
import {MutedText} from '@/components/shared/MutedText'
import {InlineRow} from '@/components/shared/InlineRow'
import {ScrollableBox} from '@/components/shared/ScrollableBox'
import {CodeBlock} from '@/components/shared/CodeBlock'
import {TreeRow} from '@/components/shared/TreeRow'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {PageLayout} from '@/components/shared/PageLayout'
import {Icon} from '@/components/shared/Icon'
import {Strong} from '@/components/shared/Strong'
import {StatusText} from '@/components/shared/StatusText'
import {ConnectionStatusBadge, ConnectionState} from '@/components/shared/ConnectionStatusBadge'
import {NumericCell} from '@/components/shared/NumericCell'
import {TreeTableContainer} from '@/components/shared/TreeTableContainer'
import {TreeTableHeader} from '@/components/shared/TreeTableHeader'
import {AuthorityViolationsSection} from '@/components/sources/AuthorityViolationsSection'

export function ExecutionDetail() {
    const {id} = useParams<{ id: string }>()
    const i18n = useI18n()
    usePageTitle(i18n.jobExecutions.detailPageTitle)
    const [logViewerTask, setLogViewerTask] = useState<{ id: string, name: string } | null>(null)

    const {execution, job, taskExecutions, isExecutionLoading: execLoading, executionError: execError} = useExecution(id)
    const {cancel: cancelMutation} = useExecutionMutations(id)
    const {canCancel} = useExecutionPermissions()

    const isActiveExecution = (status: JobExecutionStatus | undefined) =>
        status === JobExecutionStatus.RUNNING || status === JobExecutionStatus.PENDING || status === JobExecutionStatus.CANCELLING

    const {isConnected: sseConnected, error: sseError} = useExecutionStream({
        executionId: id || '',
        enabled: !!id && isActiveExecution(execution?.status),
    })

    // Build task tree from flat list
    const taskTree = useMemo(() => {
        if (!taskExecutions.length) return []
        const tasks = [...taskExecutions]
        const taskMap = new Map<string, TaskExecutionTreeItem>()
        const roots: TaskExecutionTreeItem[] = []

        tasks.forEach(task => {
            taskMap.set(task.taskInstanceKey, {...task, children: []})
        })

        tasks.forEach(task => {
            const node = taskMap.get(task.taskInstanceKey)!
            if (task.parentName) {
                const parent = taskMap.get(task.parentName)
                if (parent) {
                    parent.children = parent.children || []
                    parent.children.push(node)
                } else {
                    roots.push(node)
                }
            } else {
                roots.push(node)
            }
        })

        const sortBySortOrder = (items: TaskExecutionTreeItem[]) => {
            items.sort((a, b) => a.sortOrder - b.sortOrder)
            items.forEach(item => {
                if (item.children?.length) sortBySortOrder(item.children)
            })
        }
        sortBySortOrder(roots)

        return roots
    }, [taskExecutions])

    const sseConnectionState: ConnectionState = sseConnected
        ? ConnectionState.CONNECTED
        : sseError
            ? ConnectionState.ERROR
            : ConnectionState.CONNECTING
    const sseConnectionLabel = sseConnected
        ? (i18n.jobExecutions.liveUpdates as string)
        : sseError
            ? sseError
            : (i18n.jobExecutions.connecting as string)

    // Render a single task row with indentation
    const renderTaskRow = (task: TaskExecutionTreeItem, depth: number = 0): React.ReactNode => {
        const isGroup = task.taskType === TaskType.GROUP
        const hasChildren = task.children && task.children.length > 0
        const showProgress = task.status === TaskExecutionStatus.RUNNING && task.progress > 0
        const totalChildCount = task.completedCount + task.failedCount + task.runningCount + task.pendingCount + task.skippedCount

        return (
            <Fragment key={task.id}>
                <TreeRow depth={depth} variant="tableRow" data-testid={`task-row-${task.taskInstanceKey}`}>
                    {/* Indent indicator */}
                    {depth > 0 && (
                        <MutedText>{'\u2514'}</MutedText>
                    )}

                    {/* Task name and type */}
                    <ContentArea>
                        <InlineRow gap="sm">
                            <Strong truncate>{task.taskName || task.taskInstanceKey}</Strong>
                            {isGroup && task.skipOnFailure && (
                                <Badge variant="outline" className="text-xs">{i18n.jobExecutions.skipOnFail}</Badge>
                            )}
                        </InlineRow>
                        {task.errorMessage && (
                            <InlineRow gap="xs" className="mt-0.5">
                                <StatusText tone="destructive" size="xs" truncate title={task.errorMessage}>
                                    {task.errorMessage}
                                </StatusText>
                                <ErrorLogLink errorLogRef={task.errorLogRef} sourceType="TaskExecution" sourceId={task.id}/>
                            </InlineRow>
                        )}
                    </ContentArea>

                    {/* Item counts columns: Total, Succeeded, Failed */}
                    <NumericCell width="sm">
                        {isGroup && totalChildCount > 0 ? (
                            <MutedText>{totalChildCount}</MutedText>
                        ) : task.itemsTotal != null && task.itemsTotal > 0 ? (
                            <span>{task.itemsTotal}</span>
                        ) : showProgress ? (
                            <InlineRow gap="xs" className="justify-end">
                                <Progress value={task.progress} className="h-2 w-12"/>
                                <MutedText size="xs">{task.progress}%</MutedText>
                            </InlineRow>
                        ) : (
                            <MutedText>{'\u2014'}</MutedText>
                        )}
                    </NumericCell>
                    <NumericCell width="sm">
                        {isGroup && totalChildCount > 0 ? (
                            <StatusText tone="success">{task.completedCount}</StatusText>
                        ) : task.itemsTotal != null && task.itemsTotal > 0 ? (
                            <StatusText tone="success">{task.itemsSucceeded ?? 0}</StatusText>
                        ) : (
                            <MutedText>{'\u2014'}</MutedText>
                        )}
                    </NumericCell>
                    <NumericCell width="sm">
                        {isGroup && totalChildCount > 0 ? (
                            task.failedCount > 0
                                ? <StatusText tone="danger">{task.failedCount}</StatusText>
                                : <MutedText>{task.failedCount}</MutedText>
                        ) : task.itemsTotal != null && task.itemsTotal > 0 ? (
                            (task.itemsFailed ?? 0) > 0
                                ? <StatusText tone="danger">{task.itemsFailed ?? 0}</StatusText>
                                : <MutedText>{task.itemsFailed ?? 0}</MutedText>
                        ) : (
                            <MutedText>{'\u2014'}</MutedText>
                        )}
                    </NumericCell>

                    {/* Status */}
                    <InlineRow className="shrink-0 w-24">
                        <StatusBadge status={task.status} type={StatusBadgeType.TASK_STATUS} compact/>
                    </InlineRow>

                    {/* Duration */}
                    <NumericCell width="md">
                        <MutedText>
                            {formatDuration(task.durationMs)}
                        </MutedText>
                    </NumericCell>

                    {/* Log viewer button */}
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        data-testid={`btn-view-logs-${task.taskInstanceKey}`}
                        onClick={() => setLogViewerTask({id: task.id, name: task.taskName || task.taskInstanceKey})}
                        title={i18n.jobExecutions.viewLogs as string}
                    >
                        <Icon icon={FileText} size="sm" muted/>
                    </Button>
                </TreeRow>
                {/* Render children */}
                {hasChildren && task.children!.map(child => renderTaskRow(child, depth + 1))}
            </Fragment>
        )
    }

    return (
        <PageLayout
            variant="detail"
            backTo={routes.backgroundJobs.executions.list()}
            loading={execLoading}
            error={execError ?? (!execLoading ? notFoundIfMissing(execution, 'EXECUTION') : undefined)}
            title={i18n.jobExecutions.executionDetails}
            icon={CONCEPT_ICON.executionDetail}
            data-testid="execution-detail-title"
            actions={execution ? (
                <>
                    <StatusBadge status={execution.status} type={StatusBadgeType.EXECUTION_STATUS}/>
                    {isActiveExecution(execution.status) && (
                        <ConnectionStatusBadge state={sseConnectionState} label={sseConnectionLabel} data-testid="badge-sse-connection"/>
                    )}
                    {job && (
                        <MutedText>
                            {i18n.jobExecutions.jobLabel}{' '}
                            <TextLink data-testid="link-job-detail" to={routes.backgroundJobs.jobs.detail(job.id)} testId="link-job-detail">
                                {job.name}
                            </TextLink>
                        </MutedText>
                    )}
                    {canCancel && (execution.status === JobExecutionStatus.RUNNING || execution.status === JobExecutionStatus.PENDING) && (
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <Button
                                    data-testid="btn-cancel-execution"
                                    variant="destructive"
                                    disabled={cancelMutation.isPending}
                                >
                                    <Icon icon={XCircle} size="sm" className="mr-2"/>
                                    {i18n.jobExecutions.cancelExecution}
                                </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>{i18n.jobExecutions.cancelExecutionTitle}</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        {i18n.jobExecutions.cancelExecutionConfirm}
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>{i18n.jobExecutions.keepRunning}</AlertDialogCancel>
                                    <AlertDialogAction
                                        onClick={() => cancelMutation.mutate()}
                                        className="bg-destructive"
                                    >
                                        {i18n.jobExecutions.cancelExecution}
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    )}
                </>
            ) : undefined}
        >
            {execution && (
                <>
            {/* Execution Info */}
            <DetailGrid columns={2}>
                <CardSection
                    title={String(i18n.jobExecutions.executionInfo)}
                    data-testid="card-execution-info"
                >
                    <Stack gap="sm">
                        <InlineRow justify="between">
                            <MutedText>{i18n.common.status}</MutedText>
                            <span><StatusBadge status={execution.status} type={StatusBadgeType.EXECUTION_STATUS}/></span>
                        </InlineRow>
                        {(execution.status === JobExecutionStatus.RUNNING || execution.status === JobExecutionStatus.PENDING) && (
                            <InlineRow justify="between">
                                <MutedText>{i18n.jobExecutions.progress}</MutedText>
                                <InlineRow gap="sm" className="flex-1 max-w-[200px]">
                                    <Progress value={execution.progress ?? 0} className="h-2 flex-1"/>
                                    <MutedText size="xs" align="right" className="w-10">
                                        {execution.progress ?? 0}%
                                    </MutedText>
                                </InlineRow>
                            </InlineRow>
                        )}
                        <InlineRow justify="between">
                            <MutedText>{i18n.jobExecutions.started}</MutedText>
                            <span>{formatTimestamp(execution.startedAt)}</span>
                        </InlineRow>
                        <InlineRow justify="between">
                            <MutedText>{i18n.jobExecutions.completed}</MutedText>
                            <span>{formatTimestamp(execution.completedAt)}</span>
                        </InlineRow>
                        <InlineRow justify="between">
                            <MutedText>{i18n.jobExecutions.duration}</MutedText>
                            <span>{formatDuration(execution.durationMs)}</span>
                        </InlineRow>
                        <InlineRow justify="between">
                            <MutedText>{i18n.jobExecutions.node}</MutedText>
                            <CodeText>{execution.nodeId || '\u2014'}</CodeText>
                        </InlineRow>
                    </Stack>
                </CardSection>

                {execution.errorMessage && (
                    <Card className="border-destructive">
                        <CardHeader>
                            <InlineRow gap="sm">
                                <CardTitle data-testid="card-title-execution-error" className="text-destructive">{i18n.jobExecutions.error}</CardTitle>
                                <ErrorLogLink errorLogRef={execution.errorLogRef} sourceType="JobExecution" sourceId={execution.id}/>
                            </InlineRow>
                        </CardHeader>
                        <CardContent>
                            <CodeBlock variant="error" data-testid="execution-error-message">
                                {execution.errorMessage}
                            </CodeBlock>
                        </CardContent>
                    </Card>
                )}

                {execution.stats && Object.keys(execution.stats).length > 0 && (
                    <CardSection
                        data-testid="section-execution-statistics"
                        title={String(i18n.jobExecutions.statistics)}
                    >
                        <CodeBlock>
                            {JSON.stringify(execution.stats, null, 2)}
                        </CodeBlock>
                    </CardSection>
                )}
            </DetailGrid>

            {/* Authority Violations — Spec §6.4 (572.07). Hidden when zero. */}
            <AuthorityViolationsSection stats={execution.stats}/>

            {/* Task Executions */}
            <CardSection
                data-testid="section-task-executions"
                title={String(i18n.jobExecutions.taskExecutions)}
                description={String(i18n.jobs.taskCount({count: String(taskExecutions.length)}))}
            >
                <TreeTableContainer>
                    {/* Header */}
                    <TreeTableHeader>
                        <MutedText size="xs" weight="medium" className="flex-1">{i18n.jobExecutions.task}</MutedText>
                        <MutedText size="xs" weight="medium" align="right" className="w-16">{i18n.jobExecutions.total}</MutedText>
                        <MutedText size="xs" weight="medium" align="right" className="w-16">{i18n.jobExecutions.succeeded}</MutedText>
                        <MutedText size="xs" weight="medium" align="right" className="w-16">{i18n.jobExecutions.failed}</MutedText>
                        <MutedText size="xs" weight="medium" className="w-24">{i18n.common.status}</MutedText>
                        <MutedText size="xs" weight="medium" align="right" className="w-20">{i18n.jobExecutions.duration}</MutedText>
                        <MutedText className="w-8">{' '}</MutedText>
                    </TreeTableHeader>
                    {/* Task tree */}
                    {taskTree.length > 0 ? (
                        <ScrollableBox maxHeight="xl" className="border-0">
                            {taskTree.map(task => renderTaskRow(task, 0))}
                        </ScrollableBox>
                    ) : (
                        <MutedText centered block className="py-8">
                            {i18n.jobExecutions.noTaskExecutions}
                        </MutedText>
                    )}
                </TreeTableContainer>
            </CardSection>

            {/* Log Viewer Dialog */}
            <ExecutionLogViewer
                referenceId={logViewerTask?.id ?? null}
                referenceName={logViewerTask?.name ?? ''}
                open={!!logViewerTask}
                onOpenChange={(open) => !open && setLogViewerTask(null)}
            />
                </>
            )}
        </PageLayout>
    )
}
